/**
 * Primitive registry — maps the string name in a layout's PrimitiveInstance
 * to the React component that renders it.
 *
 * Every primitive that the Director can place must register itself here.
 * Missing entries are not fatal: the renderer substitutes <UnknownPrimitive>
 * so partial registries don't crash the page during incremental rollouts.
 */

import type { ComponentType } from 'react'

/** Props passed to every primitive at render time. */
export interface PrimitiveProps {
  /** Stable per-instance ID. Used by Director for selection. */
  id: string
  /** Per-instance config from the layout JSON. */
  config: Record<string, unknown>
}

export type PrimitiveComponent = ComponentType<PrimitiveProps>

/** Internal registry — mutate only via {@link registerPrimitive}. */
const REGISTRY = new Map<string, PrimitiveComponent>()

/** Register a primitive by name. Idempotent — re-registering replaces. */
export function registerPrimitive(name: string, component: PrimitiveComponent): void {
  REGISTRY.set(name, component)
}

/** Look up a primitive by name. Returns undefined when missing. */
export function lookupPrimitive(name: string): PrimitiveComponent | undefined {
  return REGISTRY.get(name)
}

/** Names of all currently-registered primitives. Useful for the Director palette. */
export function registeredPrimitives(): string[] {
  return Array.from(REGISTRY.keys()).sort()
}
